if (!customElements.get('cart-drawer-discount')) {
  customElements.define(
    'cart-drawer-discount',
    class CartDrawerDiscount extends HTMLElement {
      constructor() {
        super();
        this.appliedCode = null;
      }

      connectedCallback() {
        this.discountInput = this.querySelector('[data-discount-input]');
        this.applyButton = this.querySelector('[data-discount-apply]');
        this.removeButton = this.querySelector('[data-discount-remove]');
        this.messageEl = this.querySelector('[data-discount-message]');
        this.formEl = this.querySelector('[data-discount-form]');
        this.appliedEl = this.querySelector('[data-discount-applied]');
        this.appliedCodeEl = this.querySelector('[data-discount-applied-code]');
        this.shareButton = this.closest('.drawer__footer')?.querySelector('[data-cart-share]');
        this.shareFeedback = this.closest('.drawer__footer')?.querySelector('[data-cart-share-feedback]');

        this.applyButton?.addEventListener('click', this.onApply.bind(this));
        this.removeButton?.addEventListener('click', this.onRemove.bind(this));
        this.discountInput?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.onApply();
          }
        });
        this.shareButton?.addEventListener('click', this.onShare.bind(this));

        this.syncFromCart();
      }

      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
        ];
      }

      getSectionInnerHTML(html, selector) {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
      }

      async syncFromCart() {
        try {
          const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
          const applicable = cart.discount_codes?.find((discount) => discount.applicable);
          if (applicable) {
            this.setAppliedState(applicable.code);
          } else {
            this.setIdleState();
          }
        } catch (error) {
          console.error(error);
        }
      }

      setIdleState() {
        this.appliedCode = null;
        this.formEl?.removeAttribute('hidden');
        this.appliedEl?.setAttribute('hidden', '');
        this.clearMessage();
        if (this.discountInput) this.discountInput.value = '';
      }

      setAppliedState(code) {
        this.appliedCode = code;
        this.formEl?.setAttribute('hidden', '');
        this.appliedEl?.removeAttribute('hidden');
        if (this.appliedCodeEl) this.appliedCodeEl.textContent = code;
        this.showMessage(window.cartDiscountStrings.success, 'success');
      }

      showMessage(text, type) {
        if (!this.messageEl) return;
        this.messageEl.textContent = text;
        this.messageEl.hidden = false;
        this.messageEl.classList.toggle('cart-discount__message--error', type === 'error');
        this.messageEl.classList.toggle('cart-discount__message--success', type === 'success');
      }

      clearMessage() {
        if (!this.messageEl) return;
        this.messageEl.textContent = '';
        this.messageEl.hidden = true;
        this.messageEl.classList.remove('cart-discount__message--error', 'cart-discount__message--success');
      }

      setLoading(isLoading) {
        this.applyButton?.toggleAttribute('disabled', isLoading);
        this.removeButton?.toggleAttribute('disabled', isLoading);
        this.shareButton?.toggleAttribute('disabled', isLoading);
        if (this.discountInput) this.discountInput.disabled = isLoading;
      }

      async updateDiscount(discount) {
        this.setLoading(true);

        const body = JSON.stringify({
          discount,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        });

        try {
          const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });
          const parsedState = await response.json();

          if (parsedState.errors) {
            this.showMessage(parsedState.errors, 'error');
            return null;
          }

          this.getSectionsToRender().forEach((section) => {
            const sectionElement =
              document.getElementById(section.id)?.querySelector(section.selector) ||
              document.getElementById(section.id);
            if (sectionElement && parsedState.sections?.[section.section]) {
              sectionElement.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            }
          });

          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-discount' });
          return parsedState;
        } catch (error) {
          console.error(error);
          this.showMessage(window.cartStrings.error, 'error');
          return null;
        } finally {
          this.setLoading(false);
        }
      }

      async onApply() {
        const code = this.discountInput?.value.trim();
        if (!code) {
          this.showMessage(window.cartDiscountStrings.empty, 'error');
          return;
        }

        const parsedState = await this.updateDiscount(code);
        if (!parsedState) return;

        const applied = parsedState.discount_codes?.find(
          (discount) => discount.code.toLowerCase() === code.toLowerCase()
        );

        if (applied?.applicable) {
          this.setAppliedState(applied.code);
        } else {
          this.setIdleState();
          this.showMessage(window.cartDiscountStrings.error, 'error');
        }
      }

      async onRemove() {
        const parsedState = await this.updateDiscount('');
        if (!parsedState) return;
        this.setIdleState();
      }

      buildShareUrl(cart) {
        const permalink = cart.items.map((item) => `${item.variant_id}:${item.quantity}`).join(',');
        const cartPath = `/cart/${permalink}?checkout`;
        const applicable = cart.discount_codes?.find((discount) => discount.applicable);

        if (applicable?.code) {
          return `${window.shopUrl}/discount/${encodeURIComponent(applicable.code)}?redirect=${encodeURIComponent(cartPath)}`;
        }

        return `${window.shopUrl}${cartPath}`;
      }

      async copyToClipboard(text) {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          return;
        }

        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      showShareFeedback(message, isError = false) {
        if (!this.shareFeedback) return;
        this.shareFeedback.textContent = message;
        this.shareFeedback.hidden = false;
        this.shareFeedback.classList.toggle('cart-drawer__share-feedback--error', isError);

        clearTimeout(this.shareFeedbackTimeout);
        this.shareFeedbackTimeout = setTimeout(() => {
          this.shareFeedback.hidden = true;
          this.shareFeedback.textContent = '';
        }, 3500);
      }

      async onShare() {
        try {
          const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());

          if (!cart.items?.length) {
            this.showShareFeedback(window.cartDiscountStrings.shareEmpty, true);
            return;
          }

          const shareUrl = this.buildShareUrl(cart);
          await this.copyToClipboard(shareUrl);
          this.showShareFeedback(window.cartDiscountStrings.shareSuccess);
        } catch (error) {
          console.error(error);
          this.showShareFeedback(window.cartStrings.error, true);
        }
      }
    }
  );
}
